import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Mail, Globe } from 'lucide-react';

const sections = [
  { id: 'information', title: '1. Information We Collect' },
  { id: 'use', title: '2. How We Use Information' },
  { id: 'sharing', title: '3. Sharing of Information' },
  { id: 'sdks', title: '4. Third-Party SDKs' },
  { id: 'fraud-prevention', title: '5. Fraud Prevention & Security Monitoring' },
  { id: 'security', title: '6. Data Security' },
  { id: 'advertising-id', title: '7. Advertising Identifier' },
  { id: 'tracking', title: '8. Tracking Technologies' },
  { id: 'retention', title: '9. Data Retention & Account Deletion' },
  { id: 'rights', title: '10. User Rights (DPDP Act Compliance)' },
  { id: 'children', title: '11. Children' },
  { id: 'permissions', title: '12. Permissions Used' },
  { id: 'geography', title: '13. Geography of Service' },
  { id: 'partner-policies', title: '14. Compliance with Partner Policies' },
  { id: 'virtual-currency', title: '15. Virtual Currency & Rewards' },
  { id: 'grievance', title: '16. Grievance Officer' },
  { id: 'changes', title: '17. Changes to This Policy' },
  { id: 'contact', title: '18. Contact Information' },
];

const H2 = ({ id, children }) => (
  <h2
    id={id}
    className="mt-14 scroll-mt-32 text-2xl md:text-[28px] font-semibold tracking-tightest text-ink"
  >
    {children}
  </h2>
);

const H3 = ({ children }) => (
  <h3 className="mt-6 text-lg font-semibold text-ink">{children}</h3>
);

const P = ({ children }) => (
  <p className="mt-4 text-[15px] md:text-base leading-[1.8] text-ink/75">
    {children}
  </p>
);

const UL = ({ children }) => (
  <ul className="mt-4 space-y-2.5 text-[15px] md:text-base leading-[1.8] text-ink/75 pl-1">
    {children}
  </ul>
);

const LI = ({ children }) => (
  <li className="relative pl-6 before:content-[''] before:absolute before:left-0 before:top-[0.7em] before:w-2 before:h-2 before:bg-brand before:rounded-sm">
    {children}
  </li>
);

export default function Privacy() {
  return (
    <div
      data-testid="privacy-page"
      className="min-h-screen bg-white px-5 md:px-10 pt-28 md:pt-32 pb-20"
    >
      <div className="max-w-3xl mx-auto">
        <Link
          to="/"
          data-testid="privacy-back-link"
          className="inline-flex items-center gap-2 text-ink/60 hover:text-brand transition-colors text-sm"
        >
          <ArrowLeft size={16} /> Back to home
        </Link>

        <span className="mt-8 block font-pixel text-[11px] tracking-[0.25em] uppercase text-brand">
          Legal
        </span>
        <h1 className="mt-3 text-4xl md:text-6xl font-semibold tracking-tightest leading-[1.05]">
          Privacy Policy
        </h1>
        <p
          data-testid="privacy-last-updated"
          className="mt-5 font-pixel text-[11px] tracking-[0.2em] uppercase text-ink/50"
        >
          Last updated · October 21, 2026
        </p>

        <div className="mt-8 rounded-3xl bg-surface-muted border border-black/5 p-7 md:p-8">
          <p className="text-[15px] md:text-base leading-[1.8] text-ink/75">
            Coinsflu (&quot;App&quot;) is developed and operated by{' '}
            <strong>Kings Wake Digital Private Limited</strong>
            (&quot;Company&quot;, &quot;we&quot;, &quot;us&quot;,
            &quot;our&quot;). We respect user privacy and are committed to
            responsible data handling in compliance with applicable laws,
            including the Information Technology Act, 2000, the Digital
            Personal Data Protection Act, 2023 (DPDP Act), Google Play / AdMob
            policies, and the policies of our advertising and offerwall
            partners.
          </p>
          <p className="mt-4 text-[15px] md:text-base leading-[1.8] text-ink/75">
            By creating an account or using Coinsflu, you consent to data
            collection and processing as described in this Privacy Policy.
          </p>
        </div>

        {/* Table of contents */}
        <nav
          aria-label="Table of contents"
          data-testid="privacy-toc"
          className="mt-10 rounded-3xl border border-black/5 p-6 md:p-7"
        >
          <div className="font-pixel text-[10px] tracking-[0.25em] uppercase text-ink/40">
            Contents
          </div>
          <ol className="mt-4 grid sm:grid-cols-2 gap-x-6 gap-y-1.5 text-[13.5px] text-ink/70">
            {sections.map((s) => (
              <li key={s.id}>
                <a
                  href={`#${s.id}`}
                  className="hover:text-brand transition-colors"
                >
                  {s.title}
                </a>
              </li>
            ))}
          </ol>
        </nav>

        <article className="mt-4">
          <H2 id="information">1. Information We Collect</H2>

          <H3>A. Information You Provide</H3>
          <UL>
            <LI>Mobile number (signup &amp; login)</LI>
            <LI>OTP verification</LI>
            <LI>PIN created by user</LI>
            <LI>Profile image voluntarily uploaded by the user</LI>
            <LI>UPI ID provided by the user for reward redemption</LI>
          </UL>

          <H3>B. Automatically Collected Information</H3>
          <UL>
            <LI>Device model, operating system, and app version</LI>
            <LI>Advertising ID (AAID)</LI>
            <LI>IP address (used for fraud prevention and abuse detection)</LI>
            <LI>Device fingerprint signals (used solely for fraud detection)</LI>
            <LI>Usage analytics</LI>
            <LI>Crash logs (Firebase / Google Play Services)</LI>
          </UL>
          <P>
            We do not collect precise location data, SMS data, contacts,
            microphone input, or camera recordings.
          </P>

          <H2 id="use">2. How We Use Information</H2>
          <P>We use collected information to:</P>
          <UL>
            <LI>Create and manage user accounts</LI>
            <LI>Provide app features and engagement-based rewards</LI>
            <LI>
              Validate users and prevent multiple accounts or fraudulent
              activity
            </LI>
            <LI>Process reward withdrawals subject to verification</LI>
            <LI>Display advertisements and measure performance</LI>
            <LI>Maintain app security, functionality, and stability</LI>
            <LI>
              Ensure compliance with advertising partner policies and
              applicable laws
            </LI>
          </UL>
          <P>
            Legal basis for processing may include user consent, operation of
            the service requested by the user, and compliance with applicable
            legal obligations.
          </P>

          <H2 id="sharing">3. Sharing of Information</H2>
          <P>
            We may share limited information with trusted service providers as
            described below.
          </P>

          <H3>A. Advertising &amp; Offerwall Partners</H3>
          <P>
            We work with the following categories of advertising and offerwall
            partners:
          </P>
          <UL>
            <LI>
              <strong>Display advertising:</strong> Google AdMob
            </LI>
            <LI>
              <strong>Offerwall partners may include:</strong> AdGem,
              ayeT-Studios, BitLabs, Pollfish, CPX Research, TheoremReach,
              Lootably, AdGate Media, OfferToro, Adscend Media, and similar
              networks (subject to active integration).
            </LI>
          </UL>
          <P>
            Advertising partners may receive device identifiers such as
            Advertising ID (AAID), IP address, device information, and an
            internal unique user identifier required for task verification,
            ensuring users are credited correctly for their activity.
          </P>
          <P>
            <strong>Server-to-Server (S2S) Postback Verification:</strong> When
            you complete an offer through an offerwall partner, that partner
            sends a server-to-server postback to our servers containing your
            internal user ID, transaction ID, offer details, and reward amount.
            This postback is verified using a signed hash before crediting your
            account, in order to prevent fraud and ensure accurate reward
            delivery.
          </P>
          <P>
            Each offerwall partner operates under its own privacy policy,
            accessible from within the offerwall interface or on the
            partner&apos;s official website. Users interacting with offers are
            subject to the respective partner&apos;s terms.
          </P>

          <H3>B. Analytics and Crash Reporting</H3>
          <UL>
            <LI>Firebase Analytics</LI>
            <LI>Firebase Crashlytics</LI>
            <LI>Google Play Services</LI>
          </UL>
          <P>
            Information shared with these services may include device
            information, usage metrics, and diagnostic data necessary to
            maintain application performance.
          </P>
          <P>We do not sell or rent personal user data to third parties.</P>

          <H2 id="sdks">4. Third-Party SDKs</H2>
          <P>The App currently integrates the following third-party SDKs:</P>
          <UL>
            <LI>Google AdMob</LI>
            <LI>Firebase Analytics / Crashlytics</LI>
            <LI>Google Play Services</LI>
          </UL>
          <P>
            Offerwall SDKs may be integrated to provide optional promotional
            tasks. These third-party services operate under their own privacy
            policies, and users interacting with such services may be subject
            to their respective terms.
          </P>

          <H2 id="fraud-prevention">
            5. Fraud Prevention and Security Monitoring
          </H2>
          <P>
            To maintain platform integrity and comply with advertising partner
            policies, we may use device identifiers, IP address, device
            fingerprint signals, usage patterns, and behavioral signals to
            detect and prevent fraudulent activity. This includes detection of:
          </P>
          <UL>
            <LI>Multiple accounts</LI>
            <LI>Automated bots or scripts</LI>
            <LI>Emulator or virtual device usage</LI>
            <LI>Invalid advertising traffic</LI>
            <LI>Abuse of promotional reward systems</LI>
          </UL>
          <P>
            Accounts suspected of fraudulent activity may be restricted,
            suspended, or permanently removed without prior notice, and any
            pending rewards may be forfeited.
          </P>

          <H2 id="security">6. Data Security</H2>
          <P>
            We implement industry-standard security measures to protect your
            personal data, including:
          </P>
          <UL>
            <LI>Encrypted transmission via HTTPS/TLS</LI>
            <LI>Restricted access controls for internal personnel</LI>
            <LI>Secure storage of credentials and sensitive identifiers</LI>
            <LI>
              Signed verification of all server-to-server postbacks from
              advertising partners
            </LI>
          </UL>
          <P>
            While we take reasonable steps to safeguard data, no method of
            electronic transmission or storage is 100% secure.
          </P>

          <H2 id="advertising-id">7. Advertising Identifier</H2>
          <P>
            The App may use the device Advertising ID (AAID) to display
            relevant advertisements and measure advertising performance. Users
            may reset or disable their Advertising ID through their device
            settings at any time.
          </P>

          <H2 id="tracking">8. Tracking Technologies</H2>
          <P>
            The App and its partners may use device identifiers and similar
            tracking technologies to provide advertising services, measure
            campaign effectiveness, detect fraud, and improve app
            functionality.
          </P>

          <H2 id="retention">9. Data Retention &amp; Account Deletion</H2>
          <UL>
            <LI>
              Account and profile data are retained while the user account
              remains active.
            </LI>
            <LI>
              Inactive accounts may be archived after 24 months of inactivity.
            </LI>
            <LI>
              Withdrawal records may be retained for operational, accounting,
              and legal compliance purposes.
            </LI>
            <LI>
              <strong>Account Deletion:</strong> Users may request the
              permanent deletion of their account and associated personal data
              at any time — either inside the App under{' '}
              <strong>Settings → Delete Account</strong>, or on the web at{' '}
              <Link
                to="/delete-account"
                className="text-brand underline underline-offset-2"
              >
                coinsflu.kingswake.co.in/delete-account
              </Link>{' '}
              (no app installation required), or by contacting support at{' '}
              <a
                href="mailto:team@kingswake.co.in"
                className="text-brand underline underline-offset-2"
              >
                team@kingswake.co.in
              </a>
              . Deletion requests are verified using the account's registered
              credentials before any data is removed.
            </LI>
            <LI>
              <strong>What is deleted:</strong> Upon a verified deletion
              request, the account is immediately and irreversibly disabled
              and personal identifiers — name, Coinsflu ID (username), mobile
              number, email address, profile image, sign-in credentials, PIN,
              device identifiers, push-notification tokens and sessions — are
              deleted or irreversibly anonymized. The account cannot be
              recovered or used to sign in again.
            </LI>
            <LI>
              <strong>What may be retained:</strong> Records of completed
              financial transactions (for example, wallet transactions,
              withdrawal records and reward/settlement history) and
              fraud-prevention or security records may be retained in
              anonymized or minimized form where required for legitimate
              legal, accounting, tax, security, or fraud-prevention
              obligations. Such retained records are not used for any other
              purpose.
            </LI>
            <LI>
              <strong>Pending withdrawals:</strong> If a withdrawal request is
              still under review, account deletion is completed only after the
              review is settled, so that legitimate payouts are not lost.
            </LI>
          </UL>

          <H2 id="rights">10. User Rights (DPDP Act Compliance)</H2>
          <P>Users have the right to:</P>
          <UL>
            <LI>Access stored personal data</LI>
            <LI>Correct inaccurate information</LI>
            <LI>Withdraw consent for data processing</LI>
            <LI>Request erasure of data</LI>
          </UL>
          <P>
            Users may exercise these rights through the in-app settings or by
            writing to our Grievance Officer at the address provided in Section
            16.
          </P>

          <H2 id="children">11. Children</H2>
          <P>
            The App is strictly intended for users aged{' '}
            <strong>18 years or older</strong>. Users under the age of 18 are
            not permitted to register or use Coinsflu. We do not knowingly
            collect personal data from minors. If we become aware that data
            from a user under 18 has been collected, we will delete it promptly
            and terminate the account.
          </P>

          <H2 id="permissions">12. Permissions Used</H2>
          <UL>
            <LI>
              <strong>Internet</strong> – required to access services and load
              advertisements
            </LI>
            <LI>
              <strong>Storage</strong> – used only when a user uploads a
              profile image
            </LI>
            <LI>
              <strong>Notifications</strong> – used to alert users about
              program results, rewards, and announcements
            </LI>
            <LI>
              <strong>Phone State / Advertising ID access</strong> – used for
              advertising personalization, attribution, and fraud detection
            </LI>
          </UL>

          <H2 id="geography">13. Geography of Service</H2>
          <P>
            The service primarily operates within India. By using the App, you
            consent to the transfer of your data to servers located outside
            India, including the United States and the European Union,
            processed by trusted infrastructure providers such as Google,
            governed by appropriate contractual safeguards.
          </P>

          <H2 id="partner-policies">14. Compliance with Partner Policies</H2>
          <P>
            Users engaging with promotional tasks or offers must comply with
            partner policies. Manipulation of advertising systems, including
            but not limited to use of VPNs, emulators, automated scripts, or
            fake accounts, may result in reward forfeiture, account suspension,
            or permanent removal.
          </P>

          <H2 id="virtual-currency">15. Virtual Currency &amp; Rewards</H2>
          <P>
            Coinsflu is a social loyalty and engagement platform. The App uses
            the following virtual currencies:
          </P>
          <UL>
            <LI>
              <strong>VIP Coins</strong> — earned by completing in-app
              engagement tasks (such as watching ads, completing surveys, or
              installing partner applications). VIP Coins are{' '}
              <strong>non-redeemable</strong> and are used solely for unlocking
              in-app benefits, profile enhancements, and VIP status.
            </LI>
            <LI>
              <strong>W Coins</strong> — earned by participating in and winning
              daily program events. W Coins{' '}
              <strong>may be redeemed for real-world value</strong> (such as
              UPI transfers) at the published conversion rate of approximately{' '}
              <strong>1,079 W Coins = 1 USD</strong> (subject to change), in
              accordance with the App&apos;s promotional terms, eligibility
              checks, and verification requirements.
            </LI>
          </UL>
          <P>
            Reward redemption is a{' '}
            <strong>promotional benefit and is not guaranteed</strong>.
            Eligibility, conversion rates, withdrawal thresholds, processing
            times, and redemption availability may be modified, limited, or
            discontinued at any time without prior notice. Coinsflu does not
            offer employment, fixed income, or guaranteed financial returns.
          </P>
          <P>
            Withdrawals successfully processed and disbursed are final. Failed
            or reversed UPI transactions will be re-credited to the user&apos;s
            W Coin balance.
          </P>

          <H2 id="grievance">16. Grievance Officer</H2>
          <P>
            In accordance with the Information Technology Act and the DPDP Act
            2023, if you have any questions or grievances regarding your
            privacy, please contact our Grievance Officer:
          </P>
          <div className="mt-5 rounded-3xl bg-surface-muted border border-black/5 p-6 md:p-7">
            <div className="font-pixel text-[10px] tracking-[0.25em] uppercase text-ink/40">
              Grievance Officer
            </div>
            <div className="mt-4 space-y-2 text-[15px] text-ink/80">
              <div>
                <span className="text-ink/55">Company:</span>{' '}
                <strong>Kings Wake Digital Private Limited</strong>
              </div>
              <a
                href="mailto:team@kingswake.co.in"
                data-testid="privacy-grievance-email"
                className="inline-flex items-center gap-2 hover:text-brand transition-colors"
              >
                <Mail size={16} className="text-brand" />
                team@kingswake.co.in
              </a>
            </div>
          </div>

          <H2 id="changes">17. Changes to This Policy</H2>
          <P>
            The latest version of this Privacy Policy will always be available
            at:{' '}
            <a
              href="https://coinsflu.kingswake.co.in"
              className="text-brand underline underline-offset-2 break-all"
              target="_blank"
              rel="noreferrer"
            >
              https://coinsflu.kingswake.co.in
            </a>
            .
          </P>
          <P>
            Continued use of the App after updates constitutes acceptance of
            the revised Privacy Policy.
          </P>

          <H2 id="contact">18. Contact Information</H2>
          <div className="mt-5 rounded-3xl bg-surface-muted border border-black/5 p-6 md:p-7">
            <div className="font-pixel text-[10px] tracking-[0.25em] uppercase text-ink/40">
              Kings Wake Digital Private Limited
            </div>
            <div className="mt-4 space-y-3 text-[15px] text-ink/80">
              <a
                href="mailto:team@kingswake.co.in"
                data-testid="privacy-email-link"
                className="inline-flex items-center gap-2 hover:text-brand transition-colors"
              >
                <Mail size={16} className="text-brand" />
                team@kingswake.co.in
              </a>
              <div className="flex items-center gap-2">
                <Globe size={16} className="text-brand" />
                <a
                  href="https://kingswake.co.in"
                  className="hover:text-brand transition-colors"
                  target="_blank"
                  rel="noreferrer"
                >
                  https://kingswake.co.in
                </a>
              </div>
            </div>
          </div>
        </article>

        <div className="mt-16 flex items-center justify-between border-t border-black/5 pt-6">
          <Link
            to="/"
            data-testid="privacy-back-link-bottom"
            className="inline-flex items-center gap-2 text-ink/60 hover:text-brand transition-colors text-sm"
          >
            <ArrowLeft size={16} /> Back to home
          </Link>
          <Link
            to="/terms"
            data-testid="privacy-to-terms-link"
            className="text-sm text-brand hover:text-brand-hover transition-colors"
          >
            Read Terms &amp; Conditions →
          </Link>
        </div>
      </div>
    </div>
  );
}
